import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { DataTable } from 'components';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth, useSelectedLocation } from 'contexts';
import { useFetch } from 'hooks';
import useTimeZone from 'hooks/use-timezone';
import { Role } from 'types';
import { RedeliveryColumns, RedeliveryTableRow } from 'types/redelivery';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';

const RedeliveryList: FC = () => {
  const { userProfile } = useAuth();
  const { fetchCsv, fetchPaginated } = useFetch<RedeliveryTableRow>('redeliveries');
  const navigate = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const { newZonedDayJs } = useTimeZone();

  const columns = useMemo(() => Object.values(new RedeliveryColumns()), []);

  /* prettier-ignore */
  const defaultColumns = useMemo(() => [
    { field: 'redeliveryNumber', width: 120 },
    { field: 'startDate', width: 120 },
    { field: 'expirationDate', width: 120 },
    { field: 'customerName', width: 160 },
    { field: 'redeliveryDetails', width: 160 },
    { field: 'unitCount', width: 80 }
  ], []);

  const userColumns = useMemo(() => {
    return userProfile.columnPreferences
      .filter(({ table }) => table === 'REDELIVERY')
      .map(({ column, width }) => ({ field: column, width }));
  }, [userProfile.columnPreferences]);

  const initialColumns = useMemo(() => {
    return userColumns.length ? userColumns : defaultColumns;
  }, [defaultColumns, userColumns]);

  /* prettier-ignore */
  const filters: Filter[] = useMemo(() => [{
    field: 'expirationDate',
    value: newZonedDayJs().toISOString(),
    filterOperator: FilterOperator.ON_OR_AFTER
  }], [newZonedDayJs]);

  const initialFilters: FilterState = useMemo(() => {
    return { filters, logicalOperator: LogicalOperator.AND };
  }, [filters]);

  /* prettier-ignore */
  const createEnabled = useMemo(() =>
    userProfile.role === Role.ADMIN ||
    userProfile.role === Role.BILLING_ADMIN ||
    userProfile.role === Role.SENIOR_BOOKING ||
    userProfile.role === Role.BOOKING ||
    userProfile.role === Role.ADMIN_CUSTOMER ||
    userProfile.role === Role.CUSTOMER ||
    userProfile.role === Role.EXTERNAL,
  [userProfile.role]);



  const navigateToNewRedelivery = useCallback(() => {
    navigate(!selectedLocation ? '/redeliveries/new' : `/redeliveries/new?location=${selectedLocation.id}`);
  }, [navigate, selectedLocation]);

  return (
    <div className="page" id="redelivery-list">
      <div className="page-header">
        <h1>REDELIVERIES</h1>
        <div className="d-flex g-3">

          {createEnabled && (
            <button className="btn-primary" data-cy="new-redelivery-button" onClick={navigateToNewRedelivery}>
              NEW REDELIVERY
            </button>
          )}
        </div>
      </div>
      <div className="flex-fill">
        <DataTable
          tableClass="t-height"
          columns={columns}
          fetchData={fetchPaginated}
          fetchCsv={fetchCsv}
          filterByLocation
          initialColumns={initialColumns}
          initialFilters={initialFilters}
          enableSaveColumns="REDELIVERY"
        />
      </div>
      <SuccessDialog />
    </div>
  );
};

export default RedeliveryList;
